import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Boxes, Loader2, WifiOff, MapPin, Camera, ShieldCheck, ArrowLeft } from 'lucide-react';

export default function Login() {
  const { user, login, verifyOtp, error, setError } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('creds'); // 'creds' | 'otp'
  const [code, setCode] = useState('');

  useEffect(() => { if (user) navigate('/'); }, [user, navigate]);

  const submit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    const res = await login(email, password);
    setLoading(false);
    if (res?.otpRequired) { setStep('otp'); setCode(''); }
    else if (res?.success) navigate('/');
  };

  const submitOtp = async (e) => {
    e?.preventDefault();
    setLoading(true);
    const ok = await verifyOtp(email, code);
    setLoading(false);
    if (ok) navigate('/');
  };

  return (
    <div className="grid min-h-screen lg:grid-cols-2">
      <div className="relative hidden overflow-hidden lg:block">
        <img src="https://images.unsplash.com/photo-1630230568565-0f1d94bb5a04?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#0b0f17] via-[#0b0f17]/80 to-primary/30" />
        <div className="relative flex h-full flex-col justify-between p-10">
          <div className="flex items-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Boxes className="h-6 w-6" /></div>
            <span className="font-head text-2xl font-black tracking-tight">DIZITTAL INSPECT</span>
          </div>
          <div>
            <h1 className="font-head text-5xl font-black uppercase leading-none tracking-tight">Field-first<br />lift ad<br /><span className="text-primary">operations.</span></h1>
            <p className="mt-4 max-w-md text-muted-foreground">Society → Tower → Lift → Site. Install, inspect, damage &amp; repair with photo, GPS and timestamp — even offline.</p>
            <div className="mt-6 flex flex-wrap gap-3 text-xs text-slate-300">
              <span className="flex items-center gap-1.5 rounded-full border border-border bg-card/50 px-3 py-1.5"><WifiOff className="h-3.5 w-3.5" /> Offline-first queue</span>
              <span className="flex items-center gap-1.5 rounded-full border border-border bg-card/50 px-3 py-1.5"><MapPin className="h-3.5 w-3.5" /> GPS + timestamp</span>
              <span className="flex items-center gap-1.5 rounded-full border border-border bg-card/50 px-3 py-1.5"><Camera className="h-3.5 w-3.5" /> Proof capture</span>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6">
        <div className="w-full max-w-sm rise">
          <div className="mb-6 flex items-center gap-2 lg:hidden">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Boxes className="h-5 w-5" /></div>
            <span className="font-head text-xl font-black">DIZITTAL INSPECT</span>
          </div>

          {step === 'creds' ? (
            <>
              <h2 className="font-head text-3xl font-bold">Sign in</h2>
              <p className="mb-6 text-sm text-muted-foreground">Access your field operations dashboard.</p>
              <form onSubmit={submit} className="space-y-4">
                <div>
                  <Label htmlFor="email">Email</Label>
                  <Input id="email" data-testid="login-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" required />
                </div>
                <div>
                  <Label htmlFor="password">Password</Label>
                  <Input id="password" data-testid="login-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" required />
                </div>
                {error && <p data-testid="login-error" className="text-sm text-rose-400">{error}</p>}
                <Button type="submit" data-testid="login-submit-button" disabled={loading} className="h-11 w-full font-semibold">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Sign In
                </Button>
              </form>
            </>
          ) : (
            <>
              <div className="mb-2 flex items-center gap-2 text-primary"><ShieldCheck className="h-5 w-5" /><span className="overline">Two-step verification</span></div>
              <h2 className="font-head text-3xl font-bold">Enter code</h2>
              <p className="mb-6 text-sm text-muted-foreground">We emailed a 6-digit code to <span className="font-semibold text-foreground">{email}</span>. It expires in 10 minutes.</p>
              <form onSubmit={submitOtp} className="space-y-4">
                <div>
                  <Label htmlFor="otp">Verification code</Label>
                  <Input id="otp" data-testid="otp-input" inputMode="numeric" maxLength={6} value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} placeholder="000000"
                    className="text-center text-2xl font-black tracking-[0.5em]" required />
                </div>
                {error && <p data-testid="otp-error" className="text-sm text-rose-400">{error}</p>}
                <Button type="submit" data-testid="otp-submit-button" disabled={loading || code.length !== 6} className="h-11 w-full font-semibold">
                  {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Verify &amp; Sign In
                </Button>
                <button type="button" data-testid="otp-back-button" onClick={() => { setStep('creds'); setError(''); }} className="flex w-full items-center justify-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                  <ArrowLeft className="h-3.5 w-3.5" /> Back to sign in
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
